import { api } from "../lib/api";

export type ResourceType = "project" | "minute";

export type SharePermission = "view" | "edit" | "owner";
export type ShareStatus = "pending" | "accepted" | "declined" | "expired";

export type ShareOrigin = "share" | "project_owner" | "minute_owner";

export interface Share {
  id: number | null;
  resource_type: ResourceType;
  resource_id: number;
  user_email: string;
  user_id?: number | null;
  permission: SharePermission;
  status: ShareStatus;
  token?: string | null;
  invited_by_id?: number | null;
  accepted_at?: string | null;
  declined_at?: string | null;
  last_resent_at?: string | null;
  created_at?: string;
  updated_at?: string;
  origin?: ShareOrigin;
  isImplicitOwner?: boolean;
}

export interface Notification {
  id: number;
  user_id: number;
  type: "invitation" | "access_granted" | "access_revoked" | "permission_changed";
  resource_type?: ResourceType | null;
  resource_id?: number | null;
  title?: string | null;
  message?: string | null;
  metadata?: Record<string, unknown>;
  read_at?: string | null;
  created_at: string;
}

export interface InvitePayload {
  resourceType: ResourceType;
  resourceId: number;
  emails: string[];
  permission: SharePermission;
}

export async function inviteCollaborators(payload: InvitePayload) {
  const response = await api.post<Share[]>("/shares/invite", payload);
  return response.data;
}

export async function listShares(resourceType: ResourceType, resourceId: number) {
  const response = await api.get<Share[]>(`/shares/${resourceType}/${resourceId}`);
  return response.data;
}

// Buscar shares de múltiplos recursos em uma única request (otimização de performance)
export async function listSharesBulk(
  resourceType: ResourceType,
  resourceIds: number[]
): Promise<Record<string, Share[]>> {
  if (resourceIds.length === 0) {
    return {};
  }

  // Validar limite
  if (resourceIds.length > 50) {
    console.warn('Bulk shares: limiting to first 50 IDs');
    resourceIds = resourceIds.slice(0, 50);
  }

  const idsParam = resourceIds.join(',');
  const response = await api.get<Record<string, Share[]>>(
    `/shares/bulk?resourceType=${resourceType}&resourceIds=${idsParam}`
  );

  return response.data;
}

export async function updateSharePermission(shareId: number, permission: SharePermission) {
  const response = await api.put<Share>(`/shares/${shareId}/permission`, { permission });
  return response.data;
}

export async function removeShare(shareId: number) {
  await api.delete(`/shares/${shareId}`);
}

export async function resendInvite(shareId: number) {
  const response = await api.post<Share>(`/shares/${shareId}/resend`);
  return response.data;
}

export async function acceptInvite(token: string, email?: string) {
  const response = await api.get(`/shares/invites/accept/${token}`, {
    params: email ? { email } : undefined,
  });
  return response.data;
}

export async function declineInvite(token: string, reason?: string) {
  const response = await api.post(`/shares/invites/decline/${token}`, {
    reason,
  });
  return response.data;
}

export async function getNotifications(signal?: AbortSignal) {
  const response = await api.get<{ items: Notification[]; unreadCount: number }>(
    "/notifications",
    { signal }
  );
  return response.data;
}

export async function getUnreadNotificationCount(
  signal?: AbortSignal
): Promise<number> {
  const response = await api.get<{ unreadCount: number }>(
    "/notifications/unread-count",
    { signal }
  );
  return response.data.unreadCount;
}

export async function markNotificationAsRead(id: number) {
  const response = await api.put<Notification>(`/notifications/${id}/read`);
  return response.data;
}

export async function deleteNotification(id: number) {
  await api.delete(`/notifications/${id}`);
}

export interface SharedProject {
  id: number;
  share_id: number;
  name: string;
  description: string | null;
  cover_image: string | null;
  permission: SharePermission;
  shared_at: string | null;
  owner_name: string | null;
  owner_email: string;
}

/**
 * Lista todos os projetos compartilhados COM o usuário logado
 */
export async function listSharedWithMe(): Promise<SharedProject[]> {
  const response = await api.get<SharedProject[]>("/shares/shared-with-me");
  return response.data;
}
